/**
 * scent-filter.js
 *
 * Fragrance-family filtering for collection pages. Filters the WHOLE
 * collection, not just the page of products that happens to be loaded.
 *
 * How it works
 * - The first time a family is chosen (or when the pointer/focus reaches the
 *   filter row, as a head start) every page of the collection is fetched via
 *   the Section Rendering API and the product cards are kept in memory.
 * - Cards whose [data-notes] match the family are shown in the grid, split
 *   into pages, with a pagination control in the same style as the theme's.
 * - Pages never end on a part-filled row. Promo / featured blocks repeat on
 *   every page and can span several cells, so each candidate page is laid out
 *   the way CSS grid would (using the spans the browser reports) and the
 *   results are split to leave the fewest empty cells. Leftover products move
 *   to a new page, where the repeated promo block fills the row.
 * - The same split is applied to the unfiltered collection: the server pages
 *   by a fixed size, so its last page can end on a part-filled row. The plan
 *   needs only the product count, so pages the server already renders
 *   correctly are left alone and just the tail (usually the last one to three
 *   server pages) is rebuilt in the browser.
 * - State lives in the URL (?scent=woody&page=2) so refresh, share and the
 *   back button behave. Clearing the filter restores the page that was
 *   server-rendered.
 * - If the theme's own AJAX filter/sort swaps the grid, the active family is
 *   re-applied to the new result set.
 *
 * Markup contract
 * - Pill sections carry [data-scent-filter-section]; pills carry
 *   [data-scent-filter="vanilla"]; an optional "Best Sellers" reset button
 *   carries [data-scent-filter-clear].
 *     Highlights mode (Colour Me, Perfumers Choice, Milton Lloyd): active
 *       pill uses .is-active.
 *     Essentials mode: active pill uses .essentials-scent-pill--active and
 *       the reset button .essentials-filter-label--active.
 * - The product grid carries [data-scent-grid] with data-scent-page-size,
 *   data-scent-total-pages, data-scent-current-page and
 *   data-scent-total-products (see
 *   sections/collection.liquid and sections/section-essentials-collection.liquid).
 * - Product cards carry [data-grid-item] and [data-notes="vanilla,woody,"].
 */

(function () {
  'use strict';

  // The highlights section and the collection section can both include this
  // file; running twice would bind every handler twice.
  if (window.__scentFilterLoaded) return;
  window.__scentFilterLoaded = true;

  const PARAM_SCENT = 'scent';
  const PARAM_PAGE = 'page';
  const FETCH_CONCURRENCY = 4;
  // Re-splitting results to avoid empty cells may add pages beyond the natural
  // count (products / page size). Capped so a small result set isn't scattered
  // over many near-empty pages just to save a cell or two.
  const MAX_EXTRA_PAGES = 1;
  const PAGINATION_SELECTOR = '.pagination-custom, .essentials-pagination, #AjaxinatePagination, .pagination';
  const EMPTY_STATE_SELECTOR = '.no-results, .essentials-no-results';

  // Placeholder values that mean "no data" — treat as untagged
  const IGNORED_NOTES = ['see perfume notes', 'see product notes', 'various'];

  // Extra words a family pill also matches: products in the Fresh family are
  // tagged "Citrus" (the Fresh collection lives at /collections/citrus).
  const SCENT_ALIASES = { fresh: ['citrus'] };

  // Mirrors the arrows in snippets/icon-nav-arrow-*.liquid and
  // snippets/icon-pagination-arrow.liquid.
  const ARROW_PATH = 'M0.5 3.18164C0.223858 3.18164 0 3.4055 0 3.68164C0 3.95778 0.223858 4.18164 0.5 4.18164V3.68164V3.18164ZM14.8536 4.03519C15.0488 3.83993 15.0488 3.52335 14.8536 3.32809L11.6716 0.146107C11.4763 -0.0491555 11.1597 -0.0491555 10.9645 0.146107C10.7692 0.341369 10.7692 0.657951 10.9645 0.853214L13.7929 3.68164L10.9645 6.51007C10.7692 6.70533 10.7692 7.02191 10.9645 7.21717C11.1597 7.41244 11.4763 7.41244 11.6716 7.21717L14.8536 4.03519ZM0.5 3.68164V4.18164H14.5V3.68164V3.18164H0.5V3.68164Z';
  const BUBBLE_ARROW_PATH = 'M21.25 25.75C20.9739 25.75 20.75 25.9739 20.75 26.25C20.75 26.5261 20.9739 26.75 21.25 26.75V26.25V25.75ZM31.6036 26.6036C31.7988 26.4083 31.7988 26.0917 31.6036 25.8964L28.4216 22.7145C28.2263 22.5192 27.9097 22.5192 27.7145 22.7145C27.5192 22.9097 27.5192 23.2263 27.7145 23.4216L30.5429 26.25L27.7145 29.0784C27.5192 29.2737 27.5192 29.5903 27.7145 29.7855C27.9097 29.9808 28.2263 29.9808 28.4216 29.7855L31.6036 26.6036ZM21.25 26.25V26.75H31.25V26.25V25.75H21.25V26.25Z';

  const state = {
    scent: null,     // active family value, or null
    page: 1,         // page of the filtered results
    unfilteredPage: 0, // last unfiltered page shown, so clearing a filter returns there
    token: 0,        // bumped on every apply() so stale async work is dropped
    original: null,  // snapshot of the server-rendered grid (see captureOriginal)
    observed: null
  };

  let cache = { key: null, promise: null };
  let observer = null;
  let bound = false;
  let prefetched = false;
  let statusEl = null;

  // ── Small helpers ──

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $$(selector, root) {
    return Array.from((root || document).querySelectorAll(selector));
  }

  function pillActiveClass(btn) {
    return btn.classList.contains('essentials-scent-pill')
      ? 'essentials-scent-pill--active'
      : 'is-active';
  }

  function knownScents() {
    return $$('[data-scent-filter-section] [data-scent-filter]').map(function (btn) {
      return btn.getAttribute('data-scent-filter');
    });
  }

  function announce(message) {
    if (!statusEl) {
      statusEl = document.createElement('div');
      statusEl.className = 'visually-hidden';
      statusEl.setAttribute('role', 'status');
      statusEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(statusEl);
    }
    statusEl.textContent = message;
  }

  function replaceChildren(parent, nodes) {
    const fragment = document.createDocumentFragment();
    nodes.forEach(function (node) { fragment.appendChild(node); });
    while (parent.firstChild) parent.removeChild(parent.firstChild);
    parent.appendChild(fragment);
  }

  // ── URL state ──

  function readUrl() {
    const params = new URLSearchParams(window.location.search);
    const scent = (params.get(PARAM_SCENT) || '').toLowerCase();
    const page = parseInt(params.get(PARAM_PAGE), 10);
    return {
      scent: scent && knownScents().indexOf(scent) !== -1 ? scent : null,
      page: page > 0 ? page : 1
    };
  }

  function urlFor(scent, page) {
    const params = new URLSearchParams(window.location.search);
    if (scent) params.set(PARAM_SCENT, scent); else params.delete(PARAM_SCENT);
    if (page > 1) params.set(PARAM_PAGE, page); else params.delete(PARAM_PAGE);
    const query = params.toString();
    return window.location.pathname + (query ? '?' + query : '') + window.location.hash;
  }

  function writeUrl(url, mode) {
    if (mode === 'none') return;
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (url === current) return;
    window.history[mode === 'replace' ? 'replaceState' : 'pushState'](window.history.state, '', url);
  }

  // Everything in the query string that changes WHICH products are in the
  // collection (sort order, native filters) — i.e. all but our own params.
  function baseParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete(PARAM_SCENT);
    params.delete(PARAM_PAGE);
    return params;
  }

  function cacheKey() {
    const params = baseParams();
    params.sort();
    return window.location.pathname + '?' + params.toString();
  }

  // ── Grid snapshot ──

  function findGrid() {
    const grid = $('[data-scent-grid]');
    if (grid) return grid;
    const card = $('[data-grid-item]');
    return card ? card.parentElement : null;
  }

  function gridConfig(grid) {
    const pageSize = parseInt(grid.getAttribute('data-scent-page-size'), 10);
    const totalPages = parseInt(grid.getAttribute('data-scent-total-pages'), 10);
    const currentPage = parseInt(grid.getAttribute('data-scent-current-page'), 10);
    const totalProducts = parseInt(grid.getAttribute('data-scent-total-products'), 10);
    return {
      pageSize: pageSize > 0 ? pageSize : (grid.querySelectorAll('[data-grid-item]').length || 24),
      totalPages: totalPages > 0 ? totalPages : 1,
      currentPage: currentPage > 0 ? currentPage : 1,
      totalProducts: totalProducts > 0 ? totalProducts : 0
    };
  }

  // Remember what the server rendered so clearing the filter can put it back.
  function captureOriginal(grid) {
    const container = grid.closest('[data-products-grid]') || grid.parentElement;
    const children = Array.from(grid.children);
    const promos = [];

    children.forEach(function (el, index) {
      if (!el.hasAttribute('data-grid-item') && !el.matches(EMPTY_STATE_SELECTOR)) {
        promos.push({ el: el, index: index, cols: 1, rows: 1 });
      }
    });

    children.forEach(settleImages);

    const pagination = $(PAGINATION_SELECTOR, container);

    state.original = {
      grid: grid,
      container: container,
      children: children,
      promos: promos,
      pagination: pagination,
      paginationDisplay: pagination ? pagination.style.display : '',
      infinite: !!$('#AjaxinatePagination', container),
      essentials: grid.classList.contains('essentials-grid'),
      cfg: gridConfig(grid),
      countText: null,
      layoutSig: null,
      // The theme leaves a promo block out when its slot is past the last product,
      // so a partial page (the last one) can be missing some or all of them.
      promosComplete: true,
      tail: null,
      tailChecked: false
    };
    state.unfilteredPage = 0;

    const cardCount = children.filter(isCard).length;
    state.original.promosComplete = state.original.cfg.currentPage <= 1 || cardCount >= state.original.cfg.pageSize;

    observe(container);
  }

  // The theme's AJAX filter/sort replaces the container's contents, which
  // disconnects our grid. Re-apply the active family to the new grid.
  function observe(container) {
    if (state.observed === container) return;
    if (observer) observer.disconnect();
    state.observed = container;

    observer = new MutationObserver(function () {
      if (state.original && !state.original.grid.isConnected) onExternalSwap();
    });
    observer.observe(container, { childList: true });
  }

  function onExternalSwap() {
    state.original = null;
    if (!state.scent) {
      syncPills();
      setupUnfiltered();
      return;
    }
    // Native filter links drop our param: carry the family over from page 1.
    // Back/forward keeps it in the URL, so honour the page it names.
    const fromUrl = readUrl();
    if (fromUrl.scent) {
      apply(state.scent, fromUrl.page, 'none');
    } else {
      apply(state.scent, 1, 'replace');
    }
  }

  // ── Fetching the whole collection ──

  function sectionIdFor(grid) {
    const host = grid.closest('[data-section-id]');
    return host ? host.getAttribute('data-section-id') : null;
  }

  // ── Lazy-image placeholders ──
  //
  // The theme hides each lazy image (img.is-loading: opacity ~0, plus a shimmer
  // on its wrapper) until a capture-phase `load` listener on the document sees
  // it finish. That event only reaches the document while the image is in the
  // page, and the cards here are detached and reattached as the filter or page
  // changes, so an image that finishes loading while it's out of the page is
  // never marked as loaded and stays invisible. Clear the placeholder ourselves,
  // with listeners on the image itself (they fire wherever the image is).
  function clearLoading(img) {
    img.classList.remove('is-loading');
    const parent = img.parentNode;
    if (parent && parent.classList) parent.classList.remove('is-loading');
    const grandparent = parent && parent.parentNode;
    if (grandparent && grandparent.classList) grandparent.classList.remove('is-loading');
  }

  function settleImages(root) {
    const images = root.matches && root.matches('img') ? [root] : $$('img', root);

    images.forEach(function (img) {
      if (!img.classList.contains('is-loading')) return;

      // Already finished while nobody was listening.
      if (img.complete) {
        clearLoading(img);
        return;
      }

      if (img.__scentSettling) return;
      img.__scentSettling = true;
      const done = function () { clearLoading(img); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  // Images in a parsed document don't download, but the same markup cloned into
  // the live page starts downloading at once, and the theme marks the first few
  // images on every page eager / high priority. Fetching a whole collection
  // would therefore pull in hundreds of images for cards that may never be
  // shown. Defer them here, on the inert copy; prioritiseImages() lets the ones
  // that land on screen load normally.
  function deferMedia(el) {
    const images = el.matches('img') ? [el] : $$('img', el);
    images.forEach(function (img) {
      img.setAttribute('loading', 'lazy');
      img.removeAttribute('fetchpriority');
    });
    $$('video, audio', el).forEach(function (media) {
      media.setAttribute('preload', 'none');
    });
    return el;
  }

  // The first rows of a page are what people see first: load those images now,
  // and the first row ahead of everything else.
  function prioritiseImages(grid, nodes) {
    const columns = Math.max(1, window.getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length);
    nodes.slice(0, columns * 2).forEach(function (node, index) {
      const images = node.nodeType === 1 && node.matches('img') ? [node] : (node.querySelectorAll ? Array.from(node.querySelectorAll('img')) : []);
      images.forEach(function (img) {
        img.loading = 'eager';
        if (index < columns) img.setAttribute('fetchpriority', 'high');
      });
    });
  }

  function fetchPage(pageNumber, sectionId, attempt) {
    const params = baseParams();
    params.set(PARAM_PAGE, pageNumber);
    if (sectionId) params.set('sections', sectionId);

    return fetch(window.location.pathname + '?' + params.toString(), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        const html = sectionId ? JSON.parse(text)[sectionId] : text;
        if (typeof html !== 'string') throw new Error('Section missing from response');

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const root = $('[data-scent-grid]', doc) || doc;
        const cards = $$('[data-grid-item]', root).map(function (el) {
          const card = document.importNode(deferMedia(el), true);
          settleImages(card);
          return card;
        });

        // Keep the page's promo / featured blocks too, so a page the server
        // rendered empty (one past the end) can still be laid out. They're only
        // cloned into the live page if someone asks (importPromos): every page
        // carries the same ones, and cloning starts their images downloading.
        const promoSources = root === doc ? [] : Array.from(root.children).reduce(function (list, el, index) {
          if (!el.hasAttribute('data-grid-item') && !el.matches(EMPTY_STATE_SELECTOR)) {
            list.push({ el: el, index: index });
          }
          return list;
        }, []);

        cards.importPromos = function () {
          return promoSources.map(function (source) {
            const el = document.importNode(deferMedia(source.el), true);
            settleImages(el);
            return { el: el, index: source.index, cols: 1, rows: 1 };
          });
        };

        return cards;
      })
      .catch(function (error) {
        if ((attempt || 0) < 1) return fetchPage(pageNumber, sectionId, 1);
        throw error;
      });
  }

  // Fetched pages are cached per query (sort order / native filters), so a
  // failed or repeated request never refetches what it already has.
  let pageCache = { key: null, pages: {} };

  function getPage(pageNumber, grid) {
    const key = cacheKey();
    if (pageCache.key !== key) pageCache = { key: key, pages: {} };

    const store = pageCache.pages;
    if (!store[pageNumber]) {
      const promise = fetchPage(pageNumber, sectionIdFor(grid));
      store[pageNumber] = promise;
      // A failed load shouldn't be cached, so "Try again" really retries.
      promise.catch(function () {
        if (store[pageNumber] === promise) delete store[pageNumber];
      });
    }
    return store[pageNumber];
  }

  // Resolves to the product cards of pages first..last, in collection order.
  function getPages(first, last, grid) {
    const numbers = [];
    for (let n = first; n <= last; n++) numbers.push(n);
    if (!numbers.length) return Promise.resolve([]);

    const results = new Array(numbers.length);

    return new Promise(function (resolve, reject) {
      let next = 0;
      let active = 0;
      let done = 0;
      let failed = false;

      function pump() {
        while (!failed && active < FETCH_CONCURRENCY && next < numbers.length) {
          start(next++);
        }
      }

      function start(index) {
        active++;
        getPage(numbers[index], grid).then(function (cards) {
          results[index] = cards;
          active--;
          done++;
          if (done === numbers.length) {
            resolve(Array.prototype.concat.apply([], results));
          } else {
            pump();
          }
        }).catch(function (error) {
          failed = true;
          reject(error);
        });
      }

      pump();
    });
  }

  function isCard(el) {
    return el.hasAttribute('data-grid-item');
  }

  // The collection fits on the page the server rendered: nothing to fetch.
  function singlePageCards(grid) {
    const original = state.original;
    if (original && original.grid === grid && original.cfg.totalPages <= 1 && original.cfg.currentPage === 1) {
      return original.children.filter(isCard);
    }
    return null;
  }

  // Resolves to every product card in the collection, in collection order.
  function ensureIndex(grid) {
    const single = singlePageCards(grid);
    if (single) return Promise.resolve(single);
    return getPages(1, gridConfig(grid).totalPages, grid);
  }

  // Resolves to the cards from the first server page the tail covers onwards.
  function ensureTail(grid) {
    const original = state.original;
    const single = singlePageCards(grid);
    if (single) return Promise.resolve(single);
    return getPages(original.tail.firstDiff + 1, original.cfg.totalPages, grid);
  }

  // A partial server page can lack some or all of the promo blocks (see
  // captureOriginal), which every full page carries, so borrow them from page 1
  // before working out any layout.
  function ensurePromos(grid) {
    const original = state.original;
    if (!original || original.grid !== grid || original.promosComplete || !original.cfg.totalProducts) {
      return Promise.resolve();
    }

    return getPage(1, grid).then(function (cards) {
      if (state.original !== original) return;
      original.promos = cards.importPromos();
      original.promosComplete = true;
    });
  }

  // ── Matching ──

  function matchesTerm(n, s) {
    if (n === s) return true;
    const words = n.split(/[\s/,\-_]+/);
    if (words.indexOf(s) !== -1) return true;
    if (n.indexOf(s) !== -1) return true;
    return false;
  }

  function matchesScent(note, scent) {
    if (!scent || !note) return false;
    const n = note.toLowerCase().trim();
    const s = scent.toLowerCase().trim();
    return [s].concat(SCENT_ALIASES[s] || []).some(function (term) {
      return matchesTerm(n, term);
    });
  }

  function cardMatches(card, scent) {
    const raw = card.getAttribute('data-notes') || '';
    // A card with no real note data can't be confirmed as a match, so it's
    // left out like any other non-match.
    return raw.split(',')
      .map(function (n) { return n.trim(); })
      .filter(function (n) { return n && IGNORED_NOTES.indexOf(n) === -1; })
      .some(function (n) { return matchesScent(n, scent); });
  }

  // ── State changes ──

  function syncPills() {
    $$('[data-scent-filter-section] [data-scent-filter]').forEach(function (btn) {
      const on = !!state.scent && btn.getAttribute('data-scent-filter') === state.scent;
      btn.classList.toggle(pillActiveClass(btn), on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $$('[data-scent-filter-clear]').forEach(function (btn) {
      const on = !state.scent;
      btn.classList.toggle('essentials-filter-label--active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  // historyMode: 'push' | 'replace' | 'none'
  function apply(scent, page, historyMode, options) {
    state.scent = scent;
    state.page = page > 0 ? page : 1;
    syncPills();

    const grid = findGrid();
    if (!grid) return;

    if (!state.original || state.original.grid !== grid) {
      if (!scent) {
        // Nothing was ever filtered on this grid, so there's nothing to restore.
        writeUrl(urlFor(null, readUrl().page), historyMode);
        return;
      }
      captureOriginal(grid);
    }

    const token = ++state.token;

    if (!scent) {
      restoreOriginal();
      announce('Filter cleared');
      // Landed here on a filtered URL, so the unfiltered split hasn't been planned yet.
      if (!state.original.tailChecked) {
        state.original.tail = planTail(state.original);
        state.original.tailChecked = true;
      }
      // Back to the page we started on, re-split if the server's split leaves gaps.
      const back = state.unfilteredPage || state.original.cfg.currentPage;
      if (!showUnfiltered(back, historyMode, options || {})) {
        writeUrl(urlFor(null, state.original.cfg.currentPage), historyMode);
      }
      return;
    }

    writeUrl(urlFor(scent, state.page), historyMode);
    setLoading(grid, true);

    Promise.all([ensureIndex(grid), ensurePromos(grid)]).then(function (results) {
      if (token !== state.token) return;
      setLoading(grid, false);
      render(results[0], options || {});
    }).catch(function () {
      if (token !== state.token) return;
      setLoading(grid, false);
      renderError(grid);
    });
  }

  function setLoading(grid, on) {
    grid.setAttribute('aria-busy', on ? 'true' : 'false');
    grid.style.transition = 'opacity 0.2s ease';
    grid.style.opacity = on ? '0.4' : '';
    grid.style.pointerEvents = on ? 'none' : '';
    if (on) announce('Loading products');
  }

  function render(cards, options) {
    const original = state.original;
    if (!original || !original.grid.isConnected) return;

    const matches = cards.filter(function (card) { return cardMatches(card, state.scent); });
    const plan = planPages(matches.length, original);

    showPage({ cards: matches, plan: plan, base: 0, totalPages: Math.max(1, plan.length) }, options);
    updateResultsCount(matches.length);

    announce(matches.length + (matches.length === 1 ? ' product' : ' products') + ' found');
  }

  // Shows state.page. view.cards and view.plan cover the pages from index
  // view.base onwards; view.totalPages is the page count the pagination shows.
  function showPage(view, options) {
    const original = state.original;
    const grid = original.grid;

    state.page = Math.min(Math.max(state.page, 1), view.totalPages);

    const index = Math.max(0, state.page - 1 - view.base);
    const offset = view.plan.slice(0, index).reduce(function (sum, entry) { return sum + entry.count; }, 0);
    const entry = view.plan[index] || { count: 0, promos: true, stretch: null };
    const pageCards = view.cards.slice(offset, offset + entry.count);

    // Promo / featured blocks repeat on every page, as they do on the
    // collection's own pages; the page plan already accounts for their size.
    const nodes = view.cards.length === 0
      ? [buildEmptyState()]
      : arrange(pageCards, entry.promos ? original.promos : [], entry.stretch);

    applyStretch(original.promos, entry.promos ? entry.stretch : null);
    replaceChildren(grid, nodes);
    settleImages(grid);
    prioritiseImages(grid, nodes);
    renderPagination(view.totalPages);

    if (options.scroll) scrollToGrid(original.container);
  }

  // ── Page planning ──
  //
  // "N products per page" assumes every page ends on a full row, but promo and
  // featured blocks repeat on every page and can span several columns and rows,
  // so a short last page often leaves empty cells. To avoid that, each
  // candidate page is laid out the way CSS grid would place it (using the spans
  // the browser reports for the current viewport) and the results are split
  // across pages to leave as few empty cells as possible.

  // Products with the promo blocks spliced in at their original positions. A
  // stretched promo ({ which, extra, atEnd }) may instead go after the last
  // product, so it can finish the final row.
  function arrange(cards, promos, stretch) {
    const nodes = cards.slice();
    promos.forEach(function (promo, which) {
      const atEnd = stretch && stretch.which === which && stretch.atEnd;
      nodes.splice(atEnd ? nodes.length : Math.min(promo.index, nodes.length), 0, promo.el);
    });
    return nodes;
  }

  // The same page as grid items ({ cols, rows }) for working out empty cells.
  function arrangeItems(count, promos, stretch) {
    const items = [];
    for (let i = 0; i < count; i++) items.push({ cols: 1, rows: 1 });

    promos.forEach(function (promo, which) {
      const widen = stretch && stretch.which === which ? stretch.extra : 0;
      const atEnd = stretch && stretch.which === which && stretch.atEnd;
      items.splice(atEnd ? items.length : Math.min(promo.index, items.length), 0, { cols: promo.cols + widen, rows: promo.rows });
    });
    return items;
  }

  // Widens the stretched promo of a page (clears the others), so it fills the
  // cells a page would otherwise leave empty after it.
  function applyStretch(promos, stretch) {
    promos.forEach(function (promo, which) {
      promo.el.style.gridColumn = stretch && stretch.which === which ? 'span ' + (promo.cols + stretch.extra) : '';
    });
  }

  // "span 2" -> 2; "1" / "-1" -> every track; "auto" -> 1.
  function parseSpan(start, end, tracks) {
    const startSpan = /span\s+(\d+)/.exec(start || '');
    if (startSpan) return parseInt(startSpan[1], 10);

    const lineOf = function (n) { return n < 0 ? tracks + 2 + n : n; };
    const from = parseInt(start, 10);
    const to = parseInt(end, 10);
    if (!isNaN(from) && !isNaN(to) && tracks > 0) return Math.max(1, lineOf(to) - lineOf(from));

    const endSpan = /span\s+(\d+)/.exec(end || '');
    return endSpan ? parseInt(endSpan[1], 10) : 1;
  }

  // Columns, flow and promo spans as the browser lays the grid out right now.
  function readLayout(original) {
    const style = window.getComputedStyle(original.grid);
    const columns = Math.max(1, style.gridTemplateColumns.split(' ').filter(Boolean).length);

    original.promos.forEach(function (promo) {
      // A page that dropped its promo blocks has them detached; attach one out
      // of sight just long enough to read the span the current breakpoint gives it.
      const attached = promo.el.parentNode === original.grid;
      const display = promo.el.style.display;
      if (!attached) {
        promo.el.style.display = 'none';
        original.grid.appendChild(promo.el);
      }

      const stretched = promo.el.style.gridColumn;
      promo.el.style.gridColumn = '';
      const promoStyle = window.getComputedStyle(promo.el);
      promo.cols = Math.min(parseSpan(promoStyle.gridColumnStart, promoStyle.gridColumnEnd, columns), columns);
      promo.rows = parseSpan(promoStyle.gridRowStart, promoStyle.gridRowEnd, 0);
      promo.el.style.gridColumn = stretched;

      if (!attached) {
        original.grid.removeChild(promo.el);
        promo.el.style.display = display;
      }
    });

    return {
      columns: columns,
      dense: /dense/.test(style.gridAutoFlow),
      signature: columns + '|' + /dense/.test(style.gridAutoFlow) + '|' + original.promos.map(function (promo) {
        return promo.index + ':' + promo.cols + 'x' + promo.rows;
      }).join(',')
    };
  }

  // Empty cells left when `items` ({ cols, rows }) are placed by CSS grid's
  // auto-placement algorithm (sparse, or dense when grid-auto-flow says so).
  function emptyCells(items, columns, dense) {
    const taken = [];
    let cursorRow = 0;
    let cursorCol = 0;
    let rowsUsed = 0;
    let cellsUsed = 0;

    const isFree = function (row, col, cols, rows) {
      for (let r = row; r < row + rows; r++) {
        for (let c = col; c < col + cols; c++) {
          if (taken[r] && taken[r][c]) return false;
        }
      }
      return true;
    };

    items.forEach(function (item) {
      const cols = Math.min(item.cols, columns);
      let row = dense ? 0 : cursorRow;
      let col = dense ? 0 : cursorCol;

      for (;;) {
        if (col + cols > columns) { row++; col = 0; continue; }
        if (isFree(row, col, cols, item.rows)) break;
        col++;
      }

      for (let r = row; r < row + item.rows; r++) {
        taken[r] = taken[r] || [];
        for (let c = col; c < col + cols; c++) taken[r][c] = true;
      }

      cursorRow = row;
      cursorCol = col;
      rowsUsed = Math.max(rowsUsed, row + item.rows);
      cellsUsed += cols * item.rows;
    });

    return rowsUsed * columns - cellsUsed;
  }

  // How many products each page holds, whether it shows the promo blocks, and
  // whether one of them widens to finish the last row. Full pages stay full;
  // the tail is re-split (possibly onto an extra page, up to MAX_EXTRA_PAGES) so
  // no page ends on a part-filled row. Priority: fewest empty cells, then
  // keeping the promo blocks on every page, then promos at their normal size (a
  // promo only widens when that is the only way to fill the row), then fewest
  // pages, then fullest early pages. A page only drops its promo blocks when
  // that strictly leaves fewer empty cells (typically one or two products).
  function planPages(total, original) {
    if (total === 0) return [];
    if (original.infinite) return [{ count: total, promos: true, stretch: null }];

    const layout = readLayout(original);
    original.layoutSig = layout.signature;

    const pageSize = Math.max(1, original.cfg.pageSize);
    const hasPromos = original.promos.length > 0;
    const cost = [null];

    for (let n = 1; n <= pageSize; n++) {
      let withPromos = { empty: emptyCells(arrangeItems(n, original.promos, null), layout.columns, layout.dense), stretch: null };

      // Short of a full row: let a one-row promo block widen, in place or after
      // the last product, to take up the leftover cells.
      if (withPromos.empty > 0) {
        original.promos.forEach(function (promo, which) {
          if (promo.rows !== 1) return;
          for (let extra = 1; promo.cols + extra <= layout.columns; extra++) {
            [false, true].forEach(function (atEnd) {
              const stretch = { which: which, extra: extra, atEnd: atEnd };
              const empty = emptyCells(arrangeItems(n, original.promos, stretch), layout.columns, layout.dense);
              if (empty < withPromos.empty) withPromos = { empty: empty, stretch: stretch };
            });
          }
        });
      }

      cost[n] = {
        withPromos: withPromos,
        plain: hasPromos ? emptyCells(arrangeItems(n, [], null), layout.columns, layout.dense) : Infinity
      };
    }

    const minPages = Math.ceil(total / pageSize);
    const maxPages = minPages + MAX_EXTRA_PAGES;
    const isBetter = function (a, b) {
      if (!b) return true;
      if (a.empty !== b.empty) return a.empty < b.empty;
      if (a.dropped !== b.dropped) return a.dropped < b.dropped;
      return a.stretched < b.stretched;
    };

    // table[k][r]: best way to place r products on exactly k pages.
    const table = [[{ empty: 0, stretched: 0, dropped: 0 }]];
    for (let k = 1; k <= maxPages; k++) {
      table[k] = [];
      for (let r = k; r <= total; r++) {
        for (let take = Math.min(pageSize, r - (k - 1)); take >= 1; take--) {
          const rest = table[k - 1][r - take];
          if (!rest) continue;

          const withPromos = cost[take].withPromos;
          const kept = {
            empty: rest.empty + withPromos.empty,
            stretched: rest.stretched + (withPromos.stretch ? 1 : 0),
            dropped: rest.dropped,
            take: take,
            promos: true,
            stretch: withPromos.stretch
          };
          const dropped = {
            empty: rest.empty + cost[take].plain,
            stretched: rest.stretched,
            dropped: rest.dropped + 1,
            take: take,
            promos: false,
            stretch: null
          };

          if (isBetter(kept, table[k][r])) table[k][r] = kept;
          if (hasPromos && isBetter(dropped, table[k][r])) table[k][r] = dropped;
        }
      }
    }

    let pages = 0;
    for (let k = minPages; k <= maxPages; k++) {
      const candidate = table[k][total];
      if (candidate && (!pages || isBetter(candidate, table[pages][total]))) pages = k;
    }

    const plan = [];
    for (let k = pages, remaining = total; k > 0; k--) {
      const step = table[k][remaining];
      plan.push({ count: step.take, promos: step.promos, stretch: step.stretch });
      remaining -= step.take;
    }
    return plan;
  }

  function renderError(grid) {
    const box = document.createElement('div');
    box.className = 'no-results scent-filter-no-results';
    box.setAttribute('role', 'alert');
    box.style.cssText = 'grid-column: 1 / -1; width: 100%; text-align: center; padding: 60px 20px;';
    box.innerHTML = [
      '<p style="margin-bottom: 20px;"><strong style="font-size: 1.125rem;">We couldn\'t load every product just now.</strong></p>',
      '<button type="button" class="btn caps btn--primary btn--solid" data-scent-retry style="margin: 10px 8px 0; cursor: pointer;"><span>Try again</span></button>',
      state.scent ? '<button type="button" class="btn caps btn--primary btn--outline" data-scent-clear-results style="margin: 10px 8px 0; cursor: pointer;"><span>Clear filter</span></button>' : ''
    ].join('');
    replaceChildren(grid, [box]);
    renderPagination(1);
  }

  function buildEmptyState() {
    const box = document.createElement('div');
    box.className = 'no-results scent-filter-no-results';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    box.style.cssText = 'grid-column: 1 / -1; width: 100%; text-align: center; padding: 60px 20px;';
    box.innerHTML = [
      '<p style="margin-bottom: 20px;"><strong style="font-size: 1.125rem;">No products found for this fragrance family.</strong></p>',
      '<button type="button" class="btn caps btn--primary btn--solid" data-scent-clear-results style="margin-top: 10px; cursor: pointer;">',
      '  <span>Clear filter</span>',
      '</button>'
    ].join('');
    return box;
  }

  function restoreOriginal() {
    const original = state.original;
    if (!original || !original.grid.isConnected) return;

    applyStretch(original.promos, null);
    replaceChildren(original.grid, original.children);
    settleImages(original.grid);
    setLoading(original.grid, false);
    removeOwnPagination();

    if (original.pagination) original.pagination.style.display = original.paginationDisplay;
    updateResultsCount(null);
  }

  // Colour Me shows "N products" above the grid; keep it truthful.
  function updateResultsCount(count) {
    const original = state.original;
    const el = $('.collection__results-count');
    if (!el || !original) return;

    if (original.countText === null) original.countText = el.textContent;

    el.textContent = count === null
      ? original.countText
      : original.countText.replace(/\d[\d,.]*/, String(count));
  }

  function scrollToGrid(container) {
    const nav = $('[data-collection-nav]');
    const top = container.getBoundingClientRect().top - (nav ? nav.offsetHeight : 0);
    if (window.theme && typeof window.theme.scrollTo === 'function') {
      window.theme.scrollTo(top);
    } else {
      window.scrollTo({ top: top + window.scrollY, behavior: 'smooth' });
    }
  }

  // ── The unfiltered collection ──
  //
  // The server paginates by a fixed size, so the last page of a collection can
  // end on a part-filled row. The page plan needs only the product count, so
  // pages the server already renders correctly stay as they are and just the
  // tail is rebuilt here from the last few server pages.

  // The server leaves out a promo block when no product follows its position.
  function serverPromoCount(promos, count) {
    return promos.filter(function (promo, i) { return count > promo.index - i; }).length;
  }

  function planTail(original) {
    const total = original.cfg.totalProducts;
    if (!total || original.infinite) return null;

    const plan = planPages(total, original);
    const pageSize = original.cfg.pageSize;

    let firstDiff = 0;
    while (firstDiff < plan.length) {
      const serverCount = Math.min(pageSize, total - firstDiff * pageSize);
      const planned = plan[firstDiff];
      const sameProducts = planned.count === serverCount;
      const samePromos = (planned.promos ? original.promos.length : 0) === serverPromoCount(original.promos, serverCount);
      if (!sameProducts || !samePromos || planned.stretch) break;
      firstDiff++;
    }

    // The server's own split already leaves no gaps.
    if (firstDiff >= plan.length) return null;
    return { plan: plan, firstDiff: firstDiff };
  }

  function showServerView() {
    const original = state.original;
    removeOwnPagination();
    if (original.pagination) original.pagination.style.display = original.paginationDisplay;
  }

  function setupUnfiltered() {
    const grid = findGrid();
    if (!grid) return;
    if (!state.original || state.original.grid !== grid) captureOriginal(grid);

    const original = state.original;
    const token = ++state.token;
    state.page = readUrl().page;

    // The last page (or one past the end, which comes back empty) can be missing
    // promo blocks, so borrow them from page one before working out the layout.
    ensurePromos(grid).then(function () {
      if (token !== state.token || state.original !== original) return;

      original.tail = planTail(original);
      original.tailChecked = true;

      if (!original.tail) {
        // A page past the end comes back empty: go to the last real page instead.
        if (original.cfg.totalProducts > 0 && original.cfg.currentPage > original.cfg.totalPages) {
          window.location.replace(urlFor(null, original.cfg.totalPages));
          return;
        }

        // Nothing to rebuild. If a re-split page is showing and no longer applies
        // (e.g. after a resize), put the server's page back, or reload for it.
        const altered = original.children.some(function (el) { return el.parentNode !== original.grid; });
        if (altered && state.page !== original.cfg.currentPage) {
          window.location.reload();
          return;
        }
        if (altered) restoreOriginal();
        state.unfilteredPage = original.cfg.currentPage;
        showServerView();
        return;
      }

      showUnfiltered(state.page, 'replace', {});
    }).catch(function () {});
  }

  // Shows page `page` of the unfiltered collection. Returns false when that page
  // isn't ours to render (the server's own page is already right, or it needs a
  // real navigation), so the caller can leave the browser to handle it.
  function showUnfiltered(page, historyMode, options) {
    const original = state.original;
    const tail = original && original.tail;
    if (!tail || !original.grid.isConnected) return false;

    const totalPages = tail.plan.length;
    const target = Math.min(Math.max(page, 1), totalPages);
    const inTail = target - 1 >= tail.firstDiff;
    const serverPageIsRight = original.cfg.currentPage - 1 < tail.firstDiff;

    if (!inTail && !(serverPageIsRight && target === original.cfg.currentPage)) return false;

    state.scent = null;
    state.page = target;
    state.unfilteredPage = target;
    syncPills();
    const token = ++state.token;
    writeUrl(urlFor(null, target), target !== page && historyMode === 'none' ? 'replace' : historyMode);

    if (!inTail) {
      restoreOriginal();
      renderPagination(totalPages);
      return true;
    }

    const grid = original.grid;
    setLoading(grid, true);

    ensureTail(grid).then(function (cards) {
      if (token !== state.token) return;
      setLoading(grid, false);
      showPage({ cards: cards, plan: tail.plan.slice(tail.firstDiff), base: tail.firstDiff, totalPages: totalPages }, options);
    }).catch(function () {
      if (token !== state.token) return;
      setLoading(grid, false);
      renderError(grid);
    });

    return true;
  }

  // ── Pagination for the filtered results ──

  function removeOwnPagination() {
    $$('[data-scent-pagination]').forEach(function (el) { el.remove(); });
  }

  function renderPagination(totalPages) {
    const original = state.original;
    removeOwnPagination();

    // The server-rendered pagination describes the unfiltered collection.
    if (original.pagination) original.pagination.style.display = 'none';

    if (totalPages <= 1 || original.infinite) return;

    const html = original.essentials
      ? essentialsPaginationHtml(original.pagination, state.page, totalPages)
      : customPaginationHtml(original.pagination, state.page, totalPages);

    const holder = document.createElement('div');
    holder.innerHTML = html.trim();
    const nav = holder.firstElementChild;
    nav.setAttribute('data-scent-pagination', '');
    original.grid.insertAdjacentElement('afterend', nav);
  }

  function pageHref(page) {
    return urlFor(state.scent, page);
  }

  // Same windowing as Shopify's paginate.parts: first, last and two either
  // side of the current page, with a gap marker where pages are skipped.
  function pageParts(current, total) {
    const parts = [];
    const low = Math.max(1, current - 2);
    const high = Math.min(total, current + 2);

    if (low > 1) {
      parts.push(1);
      if (low > 2) parts.push('gap');
    }
    for (let i = low; i <= high; i++) parts.push(i);
    if (high < total) {
      if (high < total - 1) parts.push('gap');
      parts.push(total);
    }
    return parts;
  }

  function labelFrom(root, selector, fallback) {
    const el = $(selector, root);
    const text = el ? el.textContent.trim() : '';
    return text || fallback;
  }

  // Mirrors snippets/pagination.liquid.
  function customPaginationHtml(template, current, total) {
    const prevLabel = labelFrom(template, '.pagination-custom__prev span', 'Previous');
    const nextLabel = labelFrom(template, '.pagination-custom__next span', 'Next');
    const hasPrev = current > 1;
    const hasNext = current < total;

    const navArrow = function (flip) {
      return '<svg xmlns="http://www.w3.org/2000/svg" class="icon-nav-arrow-' + (flip ? 'left' : 'right') + '" width="15" height="8" viewBox="0 0 15 8" fill="currentColor" aria-hidden="true" focusable="false" role="presentation"' + (flip ? '' : ' style="margin-left: 10px;"') + '>' +
        (flip ? '<g transform="translate(15,0) scale(-1,1)">' : '') +
        '<path d="' + ARROW_PATH + '" fill="currentColor"/>' +
        (flip ? '</g>' : '') + '</svg>';
    };
    const bubbleArrow = function (flip) {
      return '<svg xmlns="http://www.w3.org/2000/svg" class="icon-pagination-arrow" width="18" height="12" viewBox="20 22 12 8" fill="none" aria-hidden="true" focusable="false">' +
        (flip ? '<g transform="translate(52,0) scale(-1,1)">' : '') +
        '<path d="' + BUBBLE_ARROW_PATH + '" fill="currentColor"/>' +
        (flip ? '</g>' : '') + '</svg>';
    };

    const desktopPrev = hasPrev
      ? '<a class="pagination-custom__prev" href="' + pageHref(current - 1) + '" data-scent-page="' + (current - 1) + '" title="' + prevLabel + '"><span>' + prevLabel + '</span>' + navArrow(true) + '</a>'
      : '<span class="pagination-custom__prev is-hidden"><span>' + prevLabel + '</span>' + navArrow(true) + '</span>';
    const desktopNext = hasNext
      ? '<a class="pagination-custom__next" href="' + pageHref(current + 1) + '" data-scent-page="' + (current + 1) + '" title="' + nextLabel + '"><span>' + nextLabel + '</span>' + navArrow(false) + '</a>'
      : '<span class="pagination-custom__next is-hidden"><span>' + nextLabel + '</span>' + navArrow(false) + '</span>';

    const inner = pageParts(current, total).map(function (part) {
      if (part === 'gap') return '<span class="pagination-custom__sep">…</span>';
      if (part === current) return '<span class="pagination-custom__page pagination-custom__page--active">' + part + '</span>';
      return '<a href="' + pageHref(part) + '" data-scent-page="' + part + '" title="' + part + '" class="pagination-custom__page">' + part + '</a>';
    }).join('');

    // Mobile shows a sliding window of three page bubbles.
    const mobileWindow = 3;
    let mobileStart = Math.max(1, current - 1);
    mobileStart = Math.min(mobileStart, Math.max(1, total - mobileWindow + 1));
    const mobileEnd = Math.min(total, mobileStart + mobileWindow - 1);

    let bubbles = '';
    for (let i = mobileStart; i <= mobileEnd; i++) {
      bubbles += i === current
        ? '<span class="pagination-custom__bubble pagination-custom__bubble--active" aria-current="page">' + i + '</span>'
        : '<a class="pagination-custom__bubble" href="' + pageHref(i) + '" data-scent-page="' + i + '" title="' + i + '">' + i + '</a>';
    }

    const mobilePrev = hasPrev
      ? '<a class="pagination-custom__bubble pagination-custom__bubble--arrow" href="' + pageHref(current - 1) + '" data-scent-page="' + (current - 1) + '" title="' + prevLabel + '" aria-label="' + prevLabel + '">' + bubbleArrow(true) + '</a>'
      : '';
    const mobileNext = hasNext
      ? '<a class="pagination-custom__bubble pagination-custom__bubble--arrow" href="' + pageHref(current + 1) + '" data-scent-page="' + (current + 1) + '" title="' + nextLabel + '" aria-label="' + nextLabel + '">' + bubbleArrow(false) + '</a>'
      : '';

    return '<nav class="pagination-custom">' +
      '<div class="pagination-custom__row pagination-custom__row--desktop">' + desktopPrev + '<div class="pagination-custom__inner">' + inner + '</div>' + desktopNext + '</div>' +
      '<div class="pagination-custom__row pagination-custom__row--mobile">' + mobilePrev + bubbles + mobileNext + '</div>' +
      '</nav>';
  }

  // Mirrors the pagination in sections/section-essentials-collection.liquid.
  function essentialsPaginationHtml(template, current, total) {
    const nextLabel = labelFrom(template, '.essentials-pagination__next', 'Next');

    const items = pageParts(current, total).map(function (part) {
      if (part === 'gap') return '<span class="essentials-pagination__item essentials-pagination__item--gap"><span>…</span></span>';
      if (part === current) return '<span class="essentials-pagination__item essentials-pagination__item--active" aria-current="page"><span>' + part + '</span></span>';
      return '<a class="essentials-pagination__item" href="' + pageHref(part) + '" data-scent-page="' + part + '" aria-label="' + part + '"><span>' + part + '</span></a>';
    }).join('');

    const next = current < total
      ? '<a class="essentials-pagination__next" href="' + pageHref(current + 1) + '" data-scent-page="' + (current + 1) + '">' + nextLabel + '</a>'
      : '';

    return '<nav class="essentials-pagination" aria-label="Pagination">' + items + next + '</nav>';
  }

  // ── Events ──

  function onClick(event) {
    const target = event.target;
    if (!target || !target.closest) return;

    const pill = target.closest('[data-scent-filter]');
    if (pill && pill.closest('[data-scent-filter-section]')) {
      const value = pill.getAttribute('data-scent-filter');
      apply(state.scent === value ? null : value, 1, 'push');
      return;
    }

    if (target.closest('[data-scent-filter-clear]') || target.closest('[data-scent-clear-results]')) {
      if (state.scent) apply(null, 1, 'push');
      return;
    }

    if (target.closest('[data-scent-retry]')) {
      if (state.scent) apply(state.scent, state.page, 'none');
      else showUnfiltered(state.page, 'none', {});
      return;
    }

    const pageLink = target.closest('[data-scent-pagination] a[data-scent-page]');
    if (pageLink) {
      // Leave modified clicks (new tab / window) to the browser.
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const page = parseInt(pageLink.getAttribute('data-scent-page'), 10);
      if (state.scent) {
        event.preventDefault();
        apply(state.scent, page, 'push', { scroll: true });
      } else if (showUnfiltered(page, 'push', { scroll: true })) {
        event.preventDefault();
      }
    }
  }

  function onPopState() {
    const fromUrl = readUrl();
    const original = state.original;
    const themeRefetches = !!$('[data-collection-filters-form]');

    // Moving between unfiltered pages.
    if (!fromUrl.scent && !state.scent) {
      if (original && showUnfiltered(fromUrl.page, 'none', {})) return;
      // Not a page we render: the theme's own handler refetches it, otherwise reload.
      if (original && original.cfg.currentPage !== fromUrl.page && !themeRefetches) window.location.reload();
      return;
    }

    // Back to an unfiltered entry whose page isn't the one we have rendered:
    // the theme's own handler may refetch it, otherwise reload to be safe.
    if (!fromUrl.scent && original && original.cfg.currentPage !== fromUrl.page && !original.tail && !themeRefetches) {
      window.location.reload();
      return;
    }

    apply(fromUrl.scent, fromUrl.page, 'none');
  }

  // Start loading the whole collection as soon as someone reaches for the
  // filter row, so the result is usually instant by the time they click.
  function onIntent(event) {
    const original = state.original;
    if (original && original.tail && !state.scent && event.target.closest && event.target.closest('[data-scent-pagination]')) {
      ensureTail(original.grid).catch(function () {});
      return;
    }

    if (prefetched) return;
    if (!event.target.closest || !event.target.closest('[data-scent-filter-section]')) return;
    prefetched = true;

    const connection = navigator.connection;
    if (connection && connection.saveData) return;

    const grid = findGrid();
    if (!grid) return;
    if (!state.original || state.original.grid !== grid) captureOriginal(grid);
    ensureIndex(grid).catch(function () {});
  }

  // Crossing a breakpoint changes the column count (and how wide promo blocks
  // are), so the page split has to be worked out again for the new layout.
  let resizeTimer = null;

  function onResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      const original = state.original;
      if (!original || !original.grid.isConnected) return;
      if (readLayout(original).signature === original.layoutSig) return;

      if (!state.scent) {
        setupUnfiltered();
        return;
      }

      const token = ++state.token;
      ensureIndex(original.grid).then(function (cards) {
        if (token === state.token) render(cards, {});
      }).catch(function () {});
    }, 200);
  }

  function bind() {
    if (bound) return;
    bound = true;
    window.addEventListener('resize', onResize);
    document.addEventListener('click', onClick);
    window.addEventListener('popstate', onPopState);
    document.addEventListener('pointerover', onIntent);
    document.addEventListener('focusin', onIntent);
    document.addEventListener('touchstart', onIntent, { passive: true });
  }

  function init() {
    if (!findGrid()) return;

    bind();
    state.original = null;
    prefetched = false;

    const fromUrl = readUrl();
    if (fromUrl.scent) {
      apply(fromUrl.scent, fromUrl.page, 'none');
      return;
    }

    // A pill set as active in markup (a manually authored highlight) starts filtered.
    const preActive = $$('[data-scent-filter-section] [data-scent-filter]').filter(function (btn) {
      return btn.classList.contains(pillActiveClass(btn));
    })[0];

    if (preActive) {
      apply(preActive.getAttribute('data-scent-filter'), 1, 'replace');
    } else {
      state.scent = null;
      syncPills();
      setupUnfiltered();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
